import { vec3 } from "gl-matrix";
import { useCallback, useEffect, useRef } from "react";
import { useStore } from "react-redux";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { RootState } from "app/store";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useRedirectMouseEvents } from "hooks/useRedirectMouseEvents";
import { selectIsProcoreMobileDesign } from "slices/explorer";
import { telemetry } from "telemetry";
import { intersectRayWithPlane } from "utils/math";

import { moveSvgCameraCursor } from "../render/svgUtils";
import { DRAG_THRESHOLD, getPickRay, getSvgElement } from "./markerInteractionUtils";
import {
    markersActions,
    selectAllCollections,
    selectCanDragSelectedMarker,
    selectIsEditingMarkerLocation,
    selectSelectedMarker,
} from "./markersSlice";
import { useMarkerDragHoverTracking } from "./useMarkerDragHoverTracking";
import { createCompositeId, parseCompositeId, resolveMarkerScreenPosition, scheduleEnlargedMeasurement } from "./utils";

const HOVER_MISS_THRESHOLD = 15;
const SYNTHETIC_ENTER_COOLDOWN_MS = 100;

/**
 * Hook that manages marker interaction (selection, hover, drag & drop)
 */
export function useMarkerInteraction(options?: { enableGlobalKeydownListener?: boolean }) {
    const enableGlobalKeydownListener = options?.enableGlobalKeydownListener ?? true;

    const dispatch = useAppDispatch();
    // Direct store access used for synchronous cross-instance coordination in verifyHoverState.
    // Both MarkerInteractionMode and MarkerRendererManager call this hook, each with its own
    // currentHoveredRef. Reading store.getState() synchronously during a RAF callback lets a
    // second instance detect that the first already dispatched hover, preventing duplicate
    // performHoverEnter calls within the same animation frame.
    const reduxStore = useStore<RootState>();
    const {
        state: { canvas, view, internalEventEmitter },
    } = useExplorerGlobals();
    const collections = useAppSelector(selectAllCollections);
    const selectedMarker = useAppSelector(selectSelectedMarker);
    const canDragSelectedMarker = useAppSelector(selectCanDragSelectedMarker);
    const isEditingMarkerLocation = useAppSelector(selectIsEditingMarkerLocation);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);

    // Keep refs so that closures inside event handlers always read the latest values
    // without needing the useCallback to be recreated on every render.
    const selectedMarkerRef = useRef(selectedMarker);
    selectedMarkerRef.current = selectedMarker;
    const canDragSelectedMarkerRef = useRef(canDragSelectedMarker);
    canDragSelectedMarkerRef.current = canDragSelectedMarker;
    const isMobileDesignRef = useRef(isMobileDesign);
    isMobileDesignRef.current = isMobileDesign;

    // Use hover tracking utility for surface highlighting during drag
    const { updateHoverObject, clearHoverObject } = useMarkerDragHoverTracking(view);
    const { redirectMousedown, redirectMousemove } = useRedirectMouseEvents();

    const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const currentHoveredRef = useRef<{ collectionId: string; markerId: string } | null>(null);
    const wasEnterFiredRef = useRef<{ collectionId: string; markerId: string } | null>(null);
    const pendingEnterMeasurementRef = useRef<{ cancel: () => void } | null>(null);
    const lastSelectedMarkerUpCompletedRef = useRef<{ collectionId: string; markerId: string } | null>(null);
    const collectionsRef = useRef(collections);
    collectionsRef.current = collections;

    const cursorPositionRef = useRef<{ x: number; y: number } | null>(null);
    const hoverGuardFrameRef = useRef<number | null>(null);
    const consecutiveMissCountRef = useRef(0);
    const verifyHoverStateRef = useRef<() => void>(() => {});
    // Timestamp of the last synthetic leave fired by the miss counter. Used to
    // suppress an immediate re-enter in verifyHoverState after the miss counter
    // clears hover — preventing oscillation at transparent-gap boundaries where
    // the 1.0x ↔ 1.2x scale change repeatedly crosses the pointer-events threshold.
    const syntheticLeaveCooldownRef = useRef<number>(0);

    useEffect(() => {
        const handlePointerMove = (event: PointerEvent) => {
            cursorPositionRef.current = { x: event.clientX, y: event.clientY };
            if (hoverGuardFrameRef.current !== null) {
                return;
            }
            hoverGuardFrameRef.current = requestAnimationFrame(() => {
                hoverGuardFrameRef.current = null;
                verifyHoverStateRef.current();
            });
        };
        globalThis.addEventListener("pointermove", handlePointerMove);

        return () => {
            globalThis.removeEventListener("pointermove", handlePointerMove);
            if (hoverGuardFrameRef.current !== null) {
                cancelAnimationFrame(hoverGuardFrameRef.current);
                hoverGuardFrameRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (
            !selectedMarker ||
            lastSelectedMarkerUpCompletedRef.current?.collectionId !== selectedMarker.collectionId ||
            lastSelectedMarkerUpCompletedRef.current?.markerId !== selectedMarker.markerId
        ) {
            lastSelectedMarkerUpCompletedRef.current = null;
        }
    }, [selectedMarker]);

    const dragStateRef = useRef<{
        isDragging: boolean;
        planeNormal: vec3;
        planeOffset: number;
        collectionId: string;
        markerId: string;
        originalPosition: [number, number, number];
        lastValidPosition: vec3 | null;
        cleanup: () => void;
        initialClickOffset: { x: number; y: number };
        /** The canvas bounding rect captured at pointerdown — reused for all move/up
         *  events so that coordinate translation stays in the same space throughout
         *  the drag even if getBoundingClientRect() returns different values later
         *  (e.g. due to layout shifts or CI headless-browser quirks). */
        canvasRect: DOMRect;
    } | null>(null);

    const activeCleanupFnsRef = useRef<Set<() => void>>(new Set());

    useEffect(() => {
        const cleanupFns = activeCleanupFnsRef.current;
        return () => {
            cleanupFns.forEach((cleanup) => cleanup());
            cleanupFns.clear();
        };
    }, []);

    useEffect(() => {
        return () => {
            if (leaveTimeoutRef.current) {
                clearTimeout(leaveTimeoutRef.current);
            }
            pendingEnterMeasurementRef.current?.cancel();
            pendingEnterMeasurementRef.current = null;
        };
    }, []);

    const handleMarkerDown = useCallback(
        (id: string, event: PointerEvent) => {
            const ids = parseCompositeId(id);
            if (!ids || !canvas || !view) {
                return;
            }
            const { collectionId, markerId } = ids;

            const collection = collections[collectionId];
            const marker = collection?.markers[markerId];
            if (!collection || !marker) {
                return;
            }

            event.stopPropagation();
            const startButton = event.button;

            let contextmenuPreventer: ((e: Event) => void) | null = null;
            // Prevent browser context menu on right-click immediately (contextmenu can fire before
            // our pointermove in some browsers). Add listener upfront and remove in cleanup.
            let contextmenuTarget: EventTarget | null = null;
            if (startButton === 2) {
                event.preventDefault();
                contextmenuPreventer = (ev: Event) => ev.preventDefault();
                // Use document – guaranteed to be in the event path for any right-click, capture phase runs first
                contextmenuTarget = document;
                contextmenuTarget.addEventListener("contextmenu", contextmenuPreventer, true);
            }

            dispatch(markersActions.setPointerDownOnMarker(true));

            const camera = view.renderState.camera;
            const planeNormal = vec3.create();
            const dirView = vec3.fromValues(0, 0, -1);
            vec3.transformQuat(planeNormal, dirView, camera.rotation);
            const planeOffset = -vec3.dot(planeNormal, marker.position);

            const rectStart = canvas.getBoundingClientRect();
            const startX = event.clientX - rectStart.left;
            const startY = event.clientY - rectStart.top;

            // Calculate where the user clicked relative to the marker's anchor point.
            // Note: For this interaction, markerScreenPos is treated as being in the same
            // CSS/SVG pixel coordinate space as pointer events (startX/startY).
            let initialClickOffset: { x: number; y: number } = { x: 0, y: 0 };
            try {
                const markerScreenPos = view.convert.worldSpaceToScreenSpace([marker.position], { round: false })[0];
                if (markerScreenPos) {
                    // User clicked at startX, startY (CSS px).
                    // Marker anchor is at markerScreenPos (CSS/SVG px as used elsewhere in the codebase).
                    initialClickOffset = {
                        x: startX - markerScreenPos[0],
                        y: startY - markerScreenPos[1],
                    };
                }
            } catch (e) {
                console.warn("Could not calculate initial click offset:", e);
                telemetry.trackCaughtException({
                    source: "markers.useMarkerInteraction.clickOffset",
                    category: "render",
                    error: e,
                });
            }

            let isDragging = false;
            let listenersAttached = true;
            let animationFrameId: number | null = null;
            let pendingUpdate: { x: number; y: number; requestId: number } | null = null;
            let currentRequestId = 0;
            let activePickRequestId: number | null = null;
            let hasMovedBeyondThreshold = false;
            let attemptedInvalidDrag = false;
            let hasDispatchedNavigationMousedown = false;
            let currentX = startX;
            let currentY = startY;

            let cleanup: () => void = () => {};

            const updateMarkerPosition = async () => {
                if (!pendingUpdate || !isDragging || !dragStateRef.current || !view || !canvas) {
                    animationFrameId = null;
                    return;
                }

                const { x, y, requestId } = pendingUpdate;
                pendingUpdate = null;

                if (activePickRequestId !== null) {
                    animationFrameId = null;
                    return;
                }

                activePickRequestId = requestId;
                // Use the rect captured at pointerdown (stored on dragStateRef) so
                // that width/height passed to getPickRay are consistent with the
                // coordinate space used throughout the drag.
                const rect = dragStateRef.current.canvasRect;
                let pickedPosition: vec3 | null = null;

                // Adjust pick position by the initial click offset to maintain cursor position
                // This ensures the marker doesn't shift when drag starts
                const clickOffset = dragStateRef.current.initialClickOffset;
                const pickX = x - clickOffset.x;
                const pickY = y - clickOffset.y;

                try {
                    const pickResult = await view.pick(pickX, pickY, {
                        async: true,
                        sampleDiscRadius: 4,
                    });

                    if (activePickRequestId !== requestId) {
                        activePickRequestId = null;
                        animationFrameId = null;
                        return;
                    }

                    if (pickResult?.position) {
                        pickedPosition = pickResult.position;

                        // Update hover object for surface highlighting during drag
                        updateHoverObject(pickResult.objectId);
                    } else {
                        // Clear hover if no valid pick
                        updateHoverObject(undefined);
                    }
                } catch {
                    // Pick failed, clear hover
                    updateHoverObject(undefined);
                }

                activePickRequestId = null;

                if (requestId !== currentRequestId) {
                    animationFrameId = null;
                    return;
                }

                if (!pickedPosition && dragStateRef.current) {
                    const camera = view.renderState.camera;
                    const { width: renderWidth, height: renderHeight } = view.renderState.output;

                    if (rect.width > 0 && rect.height > 0 && renderWidth > 0 && renderHeight > 0) {
                        try {
                            const ray = getPickRay(
                                camera,
                                pickX,
                                pickY,
                                rect.width,
                                rect.height,
                                renderWidth,
                                renderHeight,
                            );
                            pickedPosition = intersectRayWithPlane(
                                ray.origin,
                                ray.dir,
                                dragStateRef.current.planeNormal,
                                dragStateRef.current.planeOffset,
                            );
                        } catch (err) {
                            if (err instanceof Error && err.message.includes("pinhole")) {
                                // Expected for orthographic cameras
                            }
                            pickedPosition = dragStateRef.current.lastValidPosition;
                        }
                    } else {
                        pickedPosition = dragStateRef.current.lastValidPosition;
                    }
                }

                if (pickedPosition && dragStateRef.current) {
                    dragStateRef.current.lastValidPosition = pickedPosition;
                    const position: [number, number, number] = [
                        pickedPosition[0],
                        pickedPosition[1],
                        pickedPosition[2],
                    ];
                    dispatch(markersActions.updateEditPreview(position));
                }

                animationFrameId = null;
            };

            const handleMove = (e: PointerEvent) => {
                // Use rectStart (captured at pointerdown) instead of a fresh
                // getBoundingClientRect() so that all coordinate translations
                // within the drag stay in the same space. Re-querying the rect on
                // each move can return a shifted value on CI due to layout changes
                // triggered by React re-renders, causing pick coords to be off by
                // a fixed amount equal to the layout delta.
                currentX = e.clientX - rectStart.left;
                currentY = e.clientY - rectStart.top;
                const distance = Math.hypot(currentX - startX, currentY - startY);

                const currentSelectedMarker = selectedMarkerRef.current;
                const isMarkerSelected =
                    currentSelectedMarker?.collectionId === collectionId &&
                    currentSelectedMarker?.markerId === markerId;
                const canStartDrag = isMarkerSelected && canDragSelectedMarkerRef.current;

                if (distance >= DRAG_THRESHOLD) {
                    hasMovedBeyondThreshold = true;

                    if (!isDragging && canStartDrag && !isMobileDesignRef.current) {
                        isDragging = true;

                        dispatch(markersActions.startEditLocation({ collectionId, markerId }));

                        const svg = getSvgElement();
                        if (svg && view) {
                            moveSvgCameraCursor({ svg, view, x: -100, y: -100, canvas });
                        }

                        if (canvas) {
                            canvas.style.cursor = "grabbing";
                        }
                        if (document.body) {
                            document.body.style.cursor = "grabbing";
                        }
                        if (document.documentElement) {
                            document.documentElement.style.cursor = "grabbing";
                        }

                        dragStateRef.current = {
                            isDragging: true,
                            planeNormal,
                            planeOffset,
                            collectionId,
                            markerId,
                            originalPosition: marker.position,
                            lastValidPosition: vec3.clone(marker.position),
                            cleanup,
                            initialClickOffset,
                            canvasRect: rectStart,
                        };
                    } else if (!isDragging && !canStartDrag) {
                        attemptedInvalidDrag = true;
                        if (!hasDispatchedNavigationMousedown) {
                            hasDispatchedNavigationMousedown = true;
                            redirectMousedown(e, startButton);
                        }
                    }
                }

                if (attemptedInvalidDrag && !isDragging) {
                    e.stopPropagation();
                    e.stopImmediatePropagation();

                    // Forward pointermove as mousemove so Novorender gets movement data even when
                    // cursor is over the marker (SVG overlay). Native mousemove may not reach
                    // document on right-click for some platforms.
                    if (hasDispatchedNavigationMousedown) {
                        redirectMousemove(e);
                    }

                    const svg = getSvgElement();
                    if (svg && view) {
                        moveSvgCameraCursor({ svg, view, x: -100, y: -100, canvas });
                    }

                    if (canvas) {
                        canvas.style.setProperty("cursor", "default", "important");
                    }
                    if (document.body) {
                        document.body.style.setProperty("cursor", "default", "important");
                    }
                    if (document.documentElement) {
                        document.documentElement.style.setProperty("cursor", "default", "important");
                    }
                }

                if (isDragging) {
                    currentRequestId++;
                    pendingUpdate = { x: currentX, y: currentY, requestId: currentRequestId };
                    animationFrameId ??= requestAnimationFrame(updateMarkerPosition);
                }
            };

            const handleUp = async (e: PointerEvent) => {
                cleanup();

                if (animationFrameId !== null) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }

                if (isDragging && dragStateRef.current) {
                    const state = dragStateRef.current;
                    const { collectionId, markerId, originalPosition } = state;

                    let finalPosition: vec3 | null = null;

                    if (view && canvas) {
                        // Use the rect captured at pointerdown so the coordinate space
                        // stays consistent with how initialClickOffset was computed.
                        const rect = state.canvasRect;
                        const x = e.clientX - rect.left;
                        const y = e.clientY - rect.top;

                        // Adjust pick position by the initial click offset
                        const clickOffset = state.initialClickOffset;
                        const pickX = x - clickOffset.x;
                        const pickY = y - clickOffset.y;

                        try {
                            const pickResult = await view.pick(pickX, pickY, {
                                async: false,
                                sampleDiscRadius: 4,
                            });

                            if (pickResult?.position) {
                                finalPosition = pickResult.position;
                            }
                        } catch {
                            // Pick failed
                        }

                        if (!finalPosition) {
                            const camera = view.renderState.camera;
                            const { width: renderWidth, height: renderHeight } = view.renderState.output;

                            if (rect.width > 0 && rect.height > 0 && renderWidth > 0 && renderHeight > 0) {
                                try {
                                    const ray = getPickRay(
                                        camera,
                                        pickX,
                                        pickY,
                                        rect.width,
                                        rect.height,
                                        renderWidth,
                                        renderHeight,
                                    );
                                    finalPosition = intersectRayWithPlane(
                                        ray.origin,
                                        ray.dir,
                                        state.planeNormal,
                                        state.planeOffset,
                                    );
                                } catch (err) {
                                    if (err instanceof Error && !err.message.includes("pinhole")) {
                                        console.warn("getPickRay failed during marker drop:", err);
                                        telemetry.trackCaughtException({
                                            source: "markers.useMarkerInteraction.getPickRay",
                                            category: "render",
                                            error: err,
                                        });
                                    }
                                }
                            }
                        }
                    }

                    finalPosition ??= state.lastValidPosition;

                    if (finalPosition) {
                        const position: [number, number, number] = [
                            finalPosition[0],
                            finalPosition[1],
                            finalPosition[2],
                        ];

                        const collection = collections[collectionId];
                        const markerBeforeUpdate = collection?.markers[markerId];

                        dispatch(
                            markersActions.updateMarker({
                                collectionId,
                                markerId,
                                updates: { position },
                            }),
                        );

                        if (collection && markerBeforeUpdate) {
                            const updatedMarker = { ...markerBeforeUpdate, position };
                            const config = collection.config;

                            const originalPositionVec3 = vec3.fromValues(
                                originalPosition[0],
                                originalPosition[1],
                                originalPosition[2],
                            );

                            config?.onLocationUpdated?.(updatedMarker, originalPositionVec3);
                        }
                    }

                    dispatch(markersActions.confirmEditLocation());

                    if (canvas) {
                        canvas.style.cursor = "";
                    }
                    if (document.body) {
                        document.body.style.cursor = "";
                    }
                    if (document.documentElement) {
                        document.documentElement.style.cursor = "";
                    }

                    dragStateRef.current = null;
                    lastSelectedMarkerUpCompletedRef.current = { collectionId, markerId };
                } else if (!isDragging && !hasMovedBeyondThreshold && startButton !== 2) {
                    // Don't select on right-click; right-drag hands off to pan navigation instead
                    dispatch(markersActions.setSelectedMarker({ collectionId, markerId }));
                    dispatch(markersActions.enableDragSelectedMarker());

                    const collection = collections[collectionId];
                    const config = collection?.config;
                    const currentMarker = collection?.markers[markerId];
                    if (currentMarker) {
                        const screenPosition =
                            canvas && view
                                ? resolveMarkerScreenPosition(
                                      null,
                                      createCompositeId(collectionId, markerId),
                                      canvas,
                                      currentMarker.position,
                                      view.renderState.camera,
                                  )
                                : undefined;
                        scheduleEnlargedMeasurement({
                            isStillTarget: () => true,
                            measure: () => {
                                if (!canvas || !view) {
                                    return undefined;
                                }
                                return resolveMarkerScreenPosition(
                                    null,
                                    createCompositeId(collectionId, markerId),
                                    canvas,
                                    currentMarker.position,
                                    view.renderState.camera,
                                );
                            },
                            onDelivered: () => {},
                            deliver: (enlargedBounds) => {
                                const payload = screenPosition ? { ...screenPosition, enlargedBounds } : undefined;
                                config?.onSelect?.(currentMarker, payload);
                            },
                        });
                    }

                    lastSelectedMarkerUpCompletedRef.current = { collectionId, markerId };
                } else if (!isDragging && hasMovedBeyondThreshold) {
                    const svg = getSvgElement();
                    if (svg && view) {
                        moveSvgCameraCursor({ svg, view, x: -100, y: -100, canvas });
                    }

                    if (canvas) {
                        canvas.style.removeProperty("cursor");
                    }
                    if (document.body) {
                        document.body.style.removeProperty("cursor");
                    }
                    if (document.documentElement) {
                        document.documentElement.style.removeProperty("cursor");
                    }
                }
            };

            const handleCameraChange = async () => {
                const currentSelectedMarker = selectedMarkerRef.current;
                const isMarkerSelected =
                    currentSelectedMarker?.collectionId === collectionId &&
                    currentSelectedMarker?.markerId === markerId;
                const canStartDrag = isMarkerSelected && canDragSelectedMarkerRef.current;

                if (!isDragging && canStartDrag && !isMobileDesignRef.current && view) {
                    isDragging = true;

                    dispatch(markersActions.startEditLocation({ collectionId, markerId }));

                    const svg = getSvgElement();
                    if (svg && view) {
                        moveSvgCameraCursor({ svg, view, x: -100, y: -100, canvas });
                    }

                    if (canvas) {
                        canvas.style.cursor = "grabbing";
                    }
                    if (document.body) {
                        document.body.style.cursor = "grabbing";
                    }
                    if (document.documentElement) {
                        document.documentElement.style.cursor = "grabbing";
                    }

                    const cameraNow = view.renderState.camera;
                    const freshPlaneNormal = vec3.create();
                    vec3.transformQuat(freshPlaneNormal, vec3.fromValues(0, 0, -1), cameraNow.rotation);
                    const freshPlaneOffset = -vec3.dot(freshPlaneNormal, marker.position);

                    dragStateRef.current = {
                        isDragging: true,
                        planeNormal: freshPlaneNormal,
                        planeOffset: freshPlaneOffset,
                        collectionId,
                        markerId,
                        originalPosition: marker.position,
                        lastValidPosition: vec3.clone(marker.position),
                        cleanup,
                        initialClickOffset,
                        canvasRect: rectStart,
                    };
                }

                if (!isDragging || !dragStateRef.current || !view || !canvas) {
                    return;
                }

                const camera = view.renderState.camera;
                vec3.transformQuat(dragStateRef.current.planeNormal, vec3.fromValues(0, 0, -1), camera.rotation);
                const refPos = dragStateRef.current.lastValidPosition;
                if (refPos) {
                    dragStateRef.current.planeOffset = -vec3.dot(dragStateRef.current.planeNormal, refPos);
                }

                const pickX = currentX - dragStateRef.current.initialClickOffset.x;
                const pickY = currentY - dragStateRef.current.initialClickOffset.y;

                let pickedPosition: vec3 | null = null;
                try {
                    const pickResult = await view.pick(pickX, pickY, {
                        async: false,
                        sampleDiscRadius: 4,
                    });
                    if (pickResult?.position) {
                        pickedPosition = pickResult.position;
                        updateHoverObject(pickResult.objectId);
                    } else {
                        updateHoverObject(undefined);
                    }
                } catch {
                    updateHoverObject(undefined);
                }

                // If no position was picked (geometry), try to get a position by raycasting on a plane computed from the normal and position of the initial marker
                if (!pickedPosition) {
                    const rect = canvas.getBoundingClientRect();
                    const { width: renderWidth, height: renderHeight } = view.renderState.output;

                    if (rect.width > 0 && rect.height > 0 && renderWidth > 0 && renderHeight > 0) {
                        try {
                            const ray = getPickRay(
                                camera,
                                pickX,
                                pickY,
                                rect.width,
                                rect.height,
                                renderWidth,
                                renderHeight,
                            );
                            pickedPosition = intersectRayWithPlane(
                                ray.origin,
                                ray.dir,
                                dragStateRef.current.planeNormal,
                                dragStateRef.current.planeOffset,
                            );
                        } catch (err) {
                            if (err instanceof Error && err.message.includes("pinhole")) {
                                // Expected for orthographic cameras
                            }
                            pickedPosition = dragStateRef.current.lastValidPosition;
                        }
                    } else {
                        pickedPosition = dragStateRef.current.lastValidPosition;
                    }
                }

                if (pickedPosition && dragStateRef.current) {
                    dragStateRef.current.lastValidPosition = pickedPosition;
                    dispatch(
                        markersActions.updateEditPreview([pickedPosition[0], pickedPosition[1], pickedPosition[2]]),
                    );
                }
            };

            // Subscribe to camera movement events for perspective scaling updates
            const unsubscribeCameraUpdated = internalEventEmitter.on("cameraUpdated", () => {
                void handleCameraChange();
            });

            cleanup = () => {
                if (!listenersAttached) {
                    return;
                }
                listenersAttached = false;

                if (animationFrameId !== null) {
                    cancelAnimationFrame(animationFrameId);
                }

                if (attemptedInvalidDrag) {
                    const svg = getSvgElement();
                    if (svg && view) {
                        moveSvgCameraCursor({ svg, view, x: -100, y: -100, canvas });
                    }
                }

                // Clear hover object to remove outline when drag ends
                clearHoverObject();
                dispatch(markersActions.setPointerDownOnMarker(false));

                globalThis.removeEventListener("pointermove", handleMove);
                globalThis.removeEventListener("pointerup", handleUp);
                globalThis.removeEventListener("pointercancel", handleUp);
                globalThis.removeEventListener("lostpointercapture", handleUp);
                unsubscribeCameraUpdated();
                if (contextmenuPreventer && contextmenuTarget) {
                    contextmenuTarget.removeEventListener("contextmenu", contextmenuPreventer, true);
                }

                activeCleanupFnsRef.current.delete(cleanup);
            };

            globalThis.addEventListener("pointermove", handleMove);
            globalThis.addEventListener("pointerup", handleUp);
            globalThis.addEventListener("pointercancel", handleUp);
            globalThis.addEventListener("lostpointercapture", handleUp);

            activeCleanupFnsRef.current.add(cleanup);
            return () => {
                cleanup();
            };
        },
        [
            collections,
            dispatch,
            canvas,
            view,
            updateHoverObject,
            clearHoverObject,
            redirectMousedown,
            redirectMousemove,
            internalEventEmitter,
        ],
    );

    const performHoverLeave = useCallback(
        (collectionId: string, markerId: string) => {
            const currentHovered = currentHoveredRef.current;
            if (
                !currentHovered ||
                currentHovered.collectionId !== collectionId ||
                currentHovered.markerId !== markerId
            ) {
                return;
            }

            dispatch(markersActions.setHoveredMarker(null));
            currentHoveredRef.current = null;
            consecutiveMissCountRef.current = 0;
            pendingEnterMeasurementRef.current?.cancel();
            pendingEnterMeasurementRef.current = null;

            const wasEntered =
                wasEnterFiredRef.current?.collectionId === collectionId &&
                wasEnterFiredRef.current?.markerId === markerId;
            wasEnterFiredRef.current = null;

            if (canvas) {
                canvas.style.removeProperty("cursor");
            }
            if (document.body) {
                document.body.style.removeProperty("cursor");
            }
            if (document.documentElement) {
                document.documentElement.style.removeProperty("cursor");
            }

            const collection = collectionsRef.current[collectionId];
            const marker = collection?.markers[markerId];
            if (collection && marker && wasEntered) {
                collection.config?.onPointerLeave?.(marker);
            }
        },
        [dispatch, canvas],
    );

    const performHoverEnter = useCallback(
        (collectionId: string, markerId: string, markerElement?: Element | null) => {
            const collection = collectionsRef.current[collectionId];
            const marker = collection?.markers[markerId];
            if (!collection || !marker) {
                return;
            }

            dispatch(markersActions.setHoveredMarker({ collectionId, markerId }));
            currentHoveredRef.current = { collectionId, markerId };
            consecutiveMissCountRef.current = 0;
            wasEnterFiredRef.current = null;
            pendingEnterMeasurementRef.current?.cancel();
            pendingEnterMeasurementRef.current = null;

            const isSelected =
                selectedMarkerRef.current?.collectionId === collectionId &&
                selectedMarkerRef.current?.markerId === markerId;
            const cursor = isSelected && canDragSelectedMarkerRef.current ? "move" : "pointer";

            if (canvas) {
                canvas.style.cursor = cursor;
            }
            if (document.body) {
                document.body.style.cursor = cursor;
            }
            if (document.documentElement) {
                document.documentElement.style.cursor = cursor;
            }

            const svg = getSvgElement();
            if (svg && view) {
                moveSvgCameraCursor({ svg, view, x: -100, y: -100, canvas });
            }

            const screenPosition =
                canvas && view
                    ? resolveMarkerScreenPosition(
                          markerElement,
                          createCompositeId(collectionId, markerId),
                          canvas,
                          marker.position,
                          view.renderState.camera,
                      )
                    : undefined;

            pendingEnterMeasurementRef.current = scheduleEnlargedMeasurement({
                isStillTarget: () =>
                    currentHoveredRef.current?.collectionId === collectionId &&
                    currentHoveredRef.current?.markerId === markerId,
                measure: () => {
                    if (!canvas || !view) {
                        return undefined;
                    }
                    return resolveMarkerScreenPosition(
                        null,
                        createCompositeId(collectionId, markerId),
                        canvas,
                        marker.position,
                        view.renderState.camera,
                    );
                },
                onDelivered: () => {
                    wasEnterFiredRef.current = { collectionId, markerId };
                },
                deliver: (enlargedBounds) => {
                    pendingEnterMeasurementRef.current = null;
                    const payload = screenPosition ? { ...screenPosition, enlargedBounds } : undefined;
                    collection.config?.onPointerEnter?.(marker, payload);
                },
            });
        },
        [dispatch, canvas, view],
    );

    const handleMarkerHover = useCallback(
        (id: string, event: PointerEvent) => {
            event.stopPropagation();

            if (leaveTimeoutRef.current) {
                clearTimeout(leaveTimeoutRef.current);
                leaveTimeoutRef.current = null;
            }

            const ids = parseCompositeId(id);
            if (!ids) {
                return;
            }
            const { collectionId, markerId } = ids;

            const currentHovered = currentHoveredRef.current;
            const isSameMarker = currentHovered?.collectionId === collectionId && currentHovered?.markerId === markerId;
            if (isSameMarker) {
                consecutiveMissCountRef.current = 0;
                return;
            }

            const elementAtCursor = document.elementFromPoint(event.clientX, event.clientY);
            const markerElement = elementAtCursor?.closest("[data-id]");
            const markerElementId = markerElement?.getAttribute("data-id");
            const incomingMarkerId = createCompositeId(collectionId, markerId);

            if (markerElementId !== incomingMarkerId) {
                return;
            }

            if (currentHovered) {
                performHoverLeave(currentHovered.collectionId, currentHovered.markerId);
            }
            performHoverEnter(collectionId, markerId, markerElement);
        },
        [performHoverEnter, performHoverLeave],
    );

    const handleMarkerOut = useCallback(
        (id: string, event: PointerEvent) => {
            event.stopPropagation();
            // During pointer-down interaction (including drag initiation), keep hover state
            // stable and avoid firing synthetic/native leave callbacks that can disrupt drag flow.
            if (activeCleanupFnsRef.current.size > 0 || dragStateRef.current) {
                return;
            }

            const ids = parseCompositeId(id);
            if (!ids) {
                return;
            }
            const { collectionId, markerId } = ids;

            if (leaveTimeoutRef.current) {
                clearTimeout(leaveTimeoutRef.current);
            }

            leaveTimeoutRef.current = setTimeout(() => {
                const currentHovered = currentHoveredRef.current;

                if (currentHovered?.collectionId === collectionId && currentHovered?.markerId === markerId) {
                    performHoverLeave(collectionId, markerId);
                }

                leaveTimeoutRef.current = null;
            }, 10);
        },
        [performHoverLeave],
    );

    const verifyHoverState = useCallback(() => {
        if (dragStateRef.current || activeCleanupFnsRef.current.size > 0) {
            return;
        }

        const cursorPosition = cursorPositionRef.current;
        if (!cursorPosition) {
            return;
        }

        const elementAtCursor = document.elementFromPoint(cursorPosition.x, cursorPosition.y);
        const markerElement = elementAtCursor?.closest("[data-id]");
        const markerElementId = markerElement?.getAttribute("data-id");
        const currentHovered = currentHoveredRef.current;

        if (currentHovered) {
            const hoveredMarkerId = createCompositeId(currentHovered.collectionId, currentHovered.markerId);
            if (markerElementId === hoveredMarkerId) {
                consecutiveMissCountRef.current = 0;
                return;
            }

            consecutiveMissCountRef.current += 1;
            if (consecutiveMissCountRef.current < HOVER_MISS_THRESHOLD) {
                return;
            }

            consecutiveMissCountRef.current = 0;
            syntheticLeaveCooldownRef.current = Date.now();
            performHoverLeave(currentHovered.collectionId, currentHovered.markerId);

            if (!markerElementId) {
                return;
            }
            const ids = parseCompositeId(markerElementId);
            if (!ids) {
                return;
            }
            performHoverEnter(ids.collectionId, ids.markerId, markerElement);

            return;
        }

        consecutiveMissCountRef.current = 0;

        if (!markerElementId) {
            return;
        }

        // Guard against duplicate performHoverEnter calls across the two concurrent hook
        // instances (MarkerInteractionMode + MarkerRendererManager). Both schedule a RAF
        // on the same pointermove; both RAFs fire within one animation frame before React
        // can re-render and propagate state. Reading the Redux store directly here is
        // synchronous – it sees the hover dispatched by whichever instance fired first.
        const storeHoveredMarker = reduxStore.getState().markers.hoveredMarker;
        if (storeHoveredMarker) {
            currentHoveredRef.current = storeHoveredMarker;
            return;
        }

        // Suppress synthetic re-entry for a short window after the miss counter fired
        // a synthetic leave. This prevents oscillation at scale-transition boundaries
        // where 1.0x ↔ 1.2x redraws repeatedly move painted pixels under the cursor.
        // Native pointerenter (handleMarkerHover) is not affected by this cooldown.
        if (Date.now() - syntheticLeaveCooldownRef.current < SYNTHETIC_ENTER_COOLDOWN_MS) {
            return;
        }

        const ids = parseCompositeId(markerElementId);
        if (!ids) {
            return;
        }
        performHoverEnter(ids.collectionId, ids.markerId, markerElement);
    }, [performHoverEnter, performHoverLeave, reduxStore]);
    verifyHoverStateRef.current = verifyHoverState;

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                if (dragStateRef.current && isEditingMarkerLocation) {
                    event.preventDefault();
                    event.stopPropagation();

                    dragStateRef.current.cleanup();
                    dragStateRef.current = null;

                    if (canvas) {
                        canvas.style.cursor = "";
                    }
                    if (document.body) {
                        document.body.style.cursor = "";
                    }
                    if (document.documentElement) {
                        document.documentElement.style.cursor = "";
                    }

                    dispatch(markersActions.cancelEditLocation());

                    return;
                }

                if (selectedMarker && !isEditingMarkerLocation) {
                    event.preventDefault();
                    event.stopPropagation();

                    const { collectionId, markerId } = selectedMarker;
                    const collection = collections[collectionId];
                    const marker = collection?.markers[markerId];

                    dispatch(markersActions.deselectMarker());

                    if (collection && marker) {
                        const config = collection.config;
                        config?.onDeselect?.(marker);
                    }
                }
            }
        },
        [isEditingMarkerLocation, selectedMarker, collections, dispatch, canvas],
    );

    const reconcileHoverAfterRedraw = useCallback(() => {
        const currentHovered = currentHoveredRef.current;

        if (!currentHovered || dragStateRef.current) {
            return;
        }

        const cursorPosition = cursorPositionRef.current;
        if (!cursorPosition) {
            return;
        }

        const elementAtCursor = document.elementFromPoint(cursorPosition.x, cursorPosition.y);
        const markerElement = elementAtCursor?.closest("[data-id]");
        const markerElementId = markerElement?.getAttribute("data-id");

        const currentHoveredMarkerId = createCompositeId(currentHovered.collectionId, currentHovered.markerId);

        if (markerElementId === currentHoveredMarkerId) {
            return;
        }

        performHoverLeave(currentHovered.collectionId, currentHovered.markerId);
    }, [performHoverLeave]);

    useEffect(() => {
        if (!enableGlobalKeydownListener) {
            return;
        }

        if (isEditingMarkerLocation || selectedMarker) {
            globalThis.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            globalThis.removeEventListener("keydown", handleKeyDown);
        };
    }, [enableGlobalKeydownListener, isEditingMarkerLocation, selectedMarker, handleKeyDown]);

    return {
        handleMarkerDown,
        handleMarkerHover,
        handleMarkerOut,
        reconcileHoverAfterRedraw,
    };
}

export type MarkerEventHandlers = ReturnType<typeof useMarkerInteraction>;
